import React from 'react';
import clsx from 'clsx';
import { useIntl } from 'react-intl';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCompactDisc } from '@fortawesome/pro-light-svg-icons/faCompactDisc';
import type { PresaveFansRelease } from 'fym-shared/src/smartlinks/presaveSubscriptions';
import { TypedMemo } from '../../common/types';
import { fansReleaseReasons } from './fansReleaseReasons';
import { fansMessages as messages } from './fansMessages';
/* eslint-disable css-modules/no-unused-class -- one stylesheet per screen; each part of the screen uses its own slice of it */
import styles from './Fans.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface FansReleaseRowProps {
  release: PresaveFansRelease;
  /** Today, YYYY-MM-DD (UTC). */
  today: string;
}

/**
 * One release that went to the subscribers: the title with its source chip (Pre-save or Catalog),
 * the day, the saved and emailed counts and the reasons a fan did not get the song (design c1).
 * Below 769 px the reasons go and the counts stack (design c2).
 */
const FansReleaseRowComponent: React.FC<FansReleaseRowProps> = ({ release, today }) => {
  const intl = useIntl();
  const day =
    release.releaseDay === today
      ? intl.formatMessage(messages.today)
      : intl.formatDate(`${release.releaseDay}T00:00:00Z`, { month: 'short', day: 'numeric', timeZone: 'UTC' });
  const reasons = fansReleaseReasons(intl, release.counts);
  const saved = intl.formatNumber(release.counts.saved);
  const emailed = intl.formatNumber(release.counts.emailed);

  return (
    <div className={styles.releaseRow}>
      <span className={styles.releaseCover} aria-hidden="true">
        <FontAwesomeIcon icon={faCompactDisc} />
      </span>
      <span className={styles.releaseTitle}>
        <b>
          {release.title}
          <span className={clsx(styles.source, release.source === 'campaign' && styles.sourceLive)}>
            {intl.formatMessage(release.source === 'campaign' ? messages.sourceCampaign : messages.sourceCatalog)}
          </span>
        </b>
        <small>{release.status === 'delivering' ? intl.formatMessage(messages.savingNow, { day }) : day}</small>
      </span>
      <span className={styles.releaseNumber}>{saved}</span>
      <span className={styles.releaseNumber}>{emailed}</span>
      <span className={styles.releaseWhy}>{reasons === '' ? '—' : reasons}</span>
      <span className={styles.releasePhone}>{intl.formatMessage(messages.phoneCounts, { saved, emailed })}</span>
    </div>
  );
};

export const FansReleaseRow = TypedMemo(FansReleaseRowComponent);
